import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Button,
  Spinner,
  Alert,
  Table,
  Badge
} from 'reactstrap';
import { formatNumber } from '../../utils/formatUtils';
import financialMetricsService from '../../service/financialMetricsService';

const FinancialMetrics = () => {
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [overdueDeals, setOverdueDeals] = useState(null);
  const [showingOverdueDeals, setShowingOverdueDeals] = useState(false);
  const [loadingOverdue, setLoadingOverdue] = useState(false);
  
  // Pagination states for founded deals
  const [foundedDealsPagination, setFoundedDealsPagination] = useState({
    page: 0,
    size: 10,
    totalPages: 0,
    totalElements: 0
  });
  
  // Pagination states for overdue deals
  const [overdueDealsPagination, setOverdueDealsPagination] = useState({
    page: 0,
    size: 10,
    totalPages: 0,
    totalElements: 0
  });

  useEffect(() => {
    loadMetrics();
  }, []);

  const loadMetrics = async (page = 0) => {
    try {
      setLoading(true);
      setError(null);
      
      const paginationParams = {
        page: page,
        size: foundedDealsPagination.size,
        sort: 'publicDealId',
        direction: 'ASC'
      };
      
      const data = await financialMetricsService.getAllMetrics(paginationParams);
      setMetrics(data);
      setLastUpdated(new Date(data.lastUpdated));
      
      // Update pagination info for founded deals
      if (data.dealBreakdown) {
        setFoundedDealsPagination(prev => ({
          ...prev,
          page: data.dealBreakdown.number || 0,
          totalPages: data.dealBreakdown.totalPages || 0,
          totalElements: data.dealBreakdown.totalElements || 0
        }));
      }
      
    } catch (err) {
      console.error('Error loading collection reports:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDeal = (dealId) => {
    if (dealId) {
      navigate(`/deals/${dealId}`);
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      setError(null);
      
      const paginationParams = {
        page: foundedDealsPagination.page,
        size: foundedDealsPagination.size,
        sort: 'publicDealId',
        direction: 'ASC'
      };
      
      const data = await financialMetricsService.getAllMetrics(paginationParams);
      setMetrics(data);
      setLastUpdated(new Date(data.lastUpdated));
      
      // Update pagination info for founded deals
      if (data.dealBreakdown) {
        setFoundedDealsPagination(prev => ({
          ...prev,
          page: data.dealBreakdown.number || 0,
          totalPages: data.dealBreakdown.totalPages || 0,
          totalElements: data.dealBreakdown.totalElements || 0
        }));
      }
      
      // Reset overdue deals view
      setShowingOverdueDeals(false);
      setOverdueDeals(null);
      
    } catch (err) {
      console.error('Error refreshing collection reports:', err);
      setError(err.message);
    } finally {
      setRefreshing(false);
    }
  };

  const handleOverdueClick = async (page = 0) => {
    if (showingOverdueDeals) {
      // If already showing overdue deals, reset to normal view
      setShowingOverdueDeals(false);
      return;
    }

    try {
      setLoadingOverdue(true);
      setError(null);
      
      const paginationParams = {
        page: page,
        size: overdueDealsPagination.size,
        sort: 'overdue',
        direction: 'DESC'
      };
      
      const data = await financialMetricsService.getOverdueDeals(paginationParams);
      setOverdueDeals(data);
      setShowingOverdueDeals(true);
      
      // Update pagination info for overdue deals
      if (data) {
        setOverdueDealsPagination(prev => ({
          ...prev,
          page: data.number || 0,
          totalPages: data.totalPages || 0,
          totalElements: data.totalElements || 0
        }));
      }
      
    } catch (err) {
      console.error('Error loading overdue deals:', err);
      setError(err.message);
    } finally {
      setLoadingOverdue(false);
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '$0.00';
    return formatNumber(amount);
  };

  // Pagination handlers
  const handleFoundedDealsPageChange = (newPage) => {
    // Asegurar que newPage sea un número
    const pageNumber = typeof newPage === 'number' ? newPage : parseInt(newPage, 10) || 0;
    console.log('Loading founded deals page:', pageNumber);
    loadMetrics(pageNumber);
  };

  const handleOverdueDealsPageChange = async (newPage) => {
    try {
      setLoadingOverdue(true);
      setError(null);
      
      // Asegurar que newPage sea un número
      const pageNumber = typeof newPage === 'number' ? newPage : parseInt(newPage, 10) || 0;
      
      const paginationParams = {
        page: pageNumber,
        size: overdueDealsPagination.size,
        sort: 'overdue',
        direction: 'DESC'
      };
      
      console.log('Loading overdue deals page:', paginationParams);
      
      const data = await financialMetricsService.getOverdueDeals(paginationParams);
      setOverdueDeals(data);
      
      // Update pagination info for overdue deals
      if (data) {
        setOverdueDealsPagination(prev => ({
          ...prev,
          page: data.number || 0,
          totalPages: data.totalPages || 0,
          totalElements: data.totalElements || 0
        }));
      }
      
    } catch (err) {
      console.error('Error loading overdue deals page:', err);
      setError(err.message);
    } finally {
      setLoadingOverdue(false);
    }
  };

  // Render pagination component with ApplicationList/MerchantList style
  const renderPagination = (currentPage, totalPages, onPageChange, totalElements = 0, pageSize = 10) => {
    if (totalPages <= 1) return null;

    return (
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center px-3 py-2 border-top">
        <div className="text-muted compact-text mb-2 mb-sm-0">
          {totalElements > 0 
            ? `Showing ${formatNumber(Math.min(currentPage * pageSize + 1, totalElements))}-${formatNumber(Math.min((currentPage + 1) * pageSize, totalElements))} of ${formatNumber(totalElements)}`
            : `Showing ${formatNumber(totalPages * pageSize)} results`
          }
        </div>
        <div className="d-flex align-items-center flex-wrap">
          <Button
            color="light"
            size="sm"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 0}
            className="mr-1"
            style={{ padding: '4px 8px', fontSize: '0.75rem' }}
          >
            <i className="fas fa-chevron-left" />
          </Button>
          {/* Page numbers */}
          <div className="d-flex align-items-center">
            {(() => {
              const pages = [];
              const maxVisiblePages = 5;
              const total = Math.max(totalPages, 1);
              let startPage = Math.max(1, (currentPage + 1) - Math.floor(maxVisiblePages / 2));
              let endPage = Math.min(total, startPage + maxVisiblePages - 1);
              if (endPage - startPage + 1 < maxVisiblePages) {
                startPage = Math.max(1, endPage - maxVisiblePages + 1);
              }
              for (let i = startPage; i <= endPage; i++) {
                pages.push(i);
              }
              return pages.map(page => (
                <Button
                  key={page}
                  color={page === (currentPage + 1) ? 'primary' : 'light'}
                  size="sm"
                  onClick={() => onPageChange(page - 1)}
                  className="mx-1"
                  style={{ minWidth: '32px', padding: '4px 8px', fontSize: '0.75rem' }}
                >
                  {page}
                </Button>
              ));
            })()}
          </div>
          <Button
            color="light"
            size="sm"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage + 1 >= totalPages}
            className="ml-1"
            style={{ padding: '4px 8px', fontSize: '0.75rem' }}
          >
            <i className="fas fa-chevron-right" />
          </Button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <Container className="mt-4" fluid>
        <Row className="justify-content-center">
          <Col md="8">
            <div className="text-center">
              <Spinner color="primary" size="lg" />
              <p className="mt-3">Loading collection reports...</p>
            </div>
          </Col>
        </Row>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-4" fluid>
        <Row>
          <Col>
            <Alert color="danger">
              <h4 className="alert-heading">Error Loading Metrics</h4>
              <p>{error}</p>
              <hr />
              <Button color="danger" onClick={loadMetrics}>
                Try Again
              </Button>
            </Alert>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container className="mt-4" fluid>
      {/* Header with Refresh Button */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="mb-0">Collection reports</h2>
              {lastUpdated && (
                <p className="text-muted mb-0">
                  Last updated: {lastUpdated.toLocaleString()}
                </p>
              )}
              <small className="text-black font-weight-bold">
                Total deals: {foundedDealsPagination.totalElements}
              </small>
            </div>
            <Button
              color="primary"
              onClick={handleRefresh}
              disabled={refreshing}
              className="d-flex align-items-center"
            >
              {refreshing ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Refreshing...
                </>
              ) : (
                <>
                  <i className="ni ni-refresh mr-2"></i>
                  Refresh
                </>
              )}
            </Button>
          </div>
        </Col>
      </Row>

      {/* Portfolio Summary Cards */}
      {metrics?.portfolioSummary && (
        <Row className="mb-2">
          <Col>
            <h4 className="mb-2">Portfolio Summary</h4>
          </Col>
        </Row>
      )}

      <Row className="mb-2">
        <Col>
          <Card className="card-stats" style={{ height: '70px' }}>
            <CardBody className="py-2 text-center">
              <h6 className="text-uppercase text-muted mb-1" style={{ fontSize: '0.75rem' }}>
                Total Advanced
              </h6>
              <span className="h4 font-weight-bold mb-0">
                {formatCurrency(metrics?.portfolioSummary?.totalAdvanced)}
              </span>
            </CardBody>
          </Card>
        </Col>

        <Col>
          <Card className="card-stats" style={{ height: '70px' }}>
            <CardBody className="py-2 text-center">
              <h6 className="text-uppercase text-muted mb-1" style={{ fontSize: '0.75rem' }}>
                Total Payback
              </h6>
              <span className="h4 font-weight-bold mb-0">
                {formatCurrency(metrics?.portfolioSummary?.totalPayback)}
              </span>
            </CardBody>
          </Card>
        </Col>

        <Col>
          <Card className="card-stats" style={{ height: '70px' }}>
            <CardBody className="py-2 text-center">
              <h6 className="text-uppercase text-muted mb-1" style={{ fontSize: '0.75rem' }}>
                Total Collected
              </h6>
              <span className="h4 font-weight-bold mb-0">
                {formatCurrency(metrics?.portfolioSummary?.totalCollected)}
              </span>
            </CardBody>
          </Card>
        </Col>

        <Col>
          <Card className="card-stats" style={{ height: '70px' }}>
            <CardBody className="py-2 text-center">
              <h6 className="text-uppercase text-muted mb-1" style={{ fontSize: '0.75rem' }}>
                Still Outstanding
              </h6>
              <span className="h4 font-weight-bold mb-0">
                {formatCurrency(metrics?.portfolioSummary?.stillOutstanding)}
              </span>
            </CardBody>
          </Card>
        </Col>

        <Col>
          <Card 
            className="card-stats"
            style={{ 
              height: '70px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              backgroundColor: showingOverdueDeals ? '#e9ecef' : 'white',
              transform: showingOverdueDeals ? 'scale(0.98)' : 'scale(1)',
              boxShadow: showingOverdueDeals ? 'inset 0 2px 4px rgba(0,0,0,0.1)' : ''
            }}
            onClick={handleOverdueClick}
          >
            <CardBody className="py-2 text-center">
              <h6 className="text-uppercase text-muted mb-1" style={{ fontSize: '0.75rem' }}>
                Total Overdue
              </h6>
              {loadingOverdue ? (
                <Spinner size="sm" color="danger" />
              ) : (
                <span className="h4 font-weight-bold mb-0 text-danger">
                  {formatCurrency(metrics?.portfolioSummary?.totalOverdue)}
                </span>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Deal Breakdown Table */}
      {showingOverdueDeals && overdueDeals && overdueDeals.content && overdueDeals.content.length > 0 && (
        <>
          <Row className="mb-1">
            <Col>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h4 className="mb-0">
                    <Badge color="danger" className="mr-2">Overdue Deals</Badge>
                  </h4>
                  <p className="text-muted mb-0">
                    Deals with overdue payments
                  </p>
                  <small className="text-danger">
                    Total overdue deals: {overdueDealsPagination.totalElements}
                  </small>
                </div>
                <Button
                  color="secondary"
                  size="sm"
                  onClick={() => setShowingOverdueDeals(false)}
                >
                  <i className="ni ni-bold-left mr-1"></i>
                  Back to All Deals
                </Button>
              </div>
            </Col>
          </Row>

          <Row>
            <Col>
              <Card>
                <CardHeader className="border-0 bg-danger">
                  <Row className="align-items-center">
                    <div className="col">
                      <h3 className="mb-0 text-white">Overdue Deal Details</h3>
                    </div>
                  </Row>
                </CardHeader>
                 <Table className="align-items-center table-flush" responsive>
                   <thead className="thead-light">
                     <tr>
                       <th scope="col">Deal ID</th>
                       <th scope="col">Advanced Amount</th>
                       <th scope="col">Payback Amount</th>
                       <th scope="col">Collected Amount</th>
                       <th scope="col">Outstanding Amount</th>
                       <th scope="col">Overdue Amount</th>
                     </tr>
                   </thead>
                   <tbody>
                     {overdueDeals.content.map((deal, index) => (
                       <tr 
                         key={deal.dealId || index}
                         onClick={() => handleViewDeal(deal.publicDealId)}
                         style={{ 
                           cursor: 'pointer',
                           transition: 'background-color 0.2s ease'
                         }}
                         onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f6f9fc'}
                         onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                       >
                         <td>
                           <span className="font-weight-bold">
                             {deal.publicDealId ? `${deal.publicDealId}` : 'N/A'}
                           </span>
                         </td>
                         <td>
                           <span className="font-weight-bold text-black">
                             {formatCurrency(deal.advanced)}
                           </span>
                         </td>
                         <td>
                           <span className="font-weight-bold text-black">
                             {formatCurrency(deal.payback)}
                           </span>
                         </td>
                         <td>
                           <span className="font-weight-bold text-black">
                             {formatCurrency(deal.collected)}
                           </span>
                         </td>
                         <td>
                           <span className="font-weight-bold text-black">
                             {formatCurrency(deal.outstanding)}
                           </span>
                         </td>
                         <td>
                           <span className="font-weight-bold text-danger">
                             {formatCurrency(deal.overdue)}
                           </span>
                         </td>
                       </tr>
                     ))}
                   </tbody>
                 </Table>
                {renderPagination(overdueDealsPagination.page, overdueDealsPagination.totalPages, handleOverdueDealsPageChange, overdueDealsPagination.totalElements, overdueDealsPagination.size)}
              </Card>
            </Col>
          </Row>
        </>
      )}

      {!showingOverdueDeals && metrics?.dealBreakdown && metrics.dealBreakdown.content && metrics.dealBreakdown.content.length > 0 && (
        <>
          <Row className="mb-1">
            <Col>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h4 className="mb-0">Funded Deals</h4>
                  <p className="text-muted mb-0">
                    Detailed financial breakdown by deal
                  </p>
                </div>
              </div>
            </Col>
          </Row>

          <Row>
            <Col>
              <Card>
                <CardHeader className="border-0">
                  <Row className="align-items-center">
                    <div className="col">
                      <h3 className="mb-0">Details</h3>
                    </div>
                  </Row>
                </CardHeader>
                 <Table className="align-items-center table-flush" responsive>
                   <thead className="thead-light">
                     <tr>
                       <th scope="col">Deal ID</th>
                       <th scope="col">Advanced Amount</th>
                       <th scope="col">Payback Amount</th>
                       <th scope="col">Collected Amount</th>
                       <th scope="col">Outstanding Amount</th>
                       <th scope="col">Overdue Amount</th>
                     </tr>
                   </thead>
                   <tbody>
                     {metrics.dealBreakdown.content.map((deal, index) => (
                       <tr 
                         key={deal.dealId || index}
                         onClick={() => handleViewDeal(deal.publicDealId)}
                         style={{ 
                           cursor: 'pointer',
                           transition: 'background-color 0.2s ease'
                         }}
                         onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f6f9fc'}
                         onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                       >
                         <td>
                           <span className="font-weight-bold">
                             {deal.publicDealId ? `${deal.publicDealId}` : 'N/A'}
                           </span>
                         </td>
                         <td>
                           <span className="font-weight-bold text-black">
                             {formatCurrency(deal.advanced)}
                           </span>
                         </td>
                         <td>
                           <span className="font-weight-bold text-black">
                             {formatCurrency(deal.payback)}
                           </span>
                         </td>
                         <td>
                           <span className="font-weight-bold text-black">
                             {formatCurrency(deal.collected)}
                           </span>
                         </td>
                         <td>
                           <span className="font-weight-bold text-black">
                             {formatCurrency(deal.outstanding)}
                           </span>
                         </td>
                         <td>
                           <span className="font-weight-bold text-danger">
                             {formatCurrency(deal.overdue)}
                           </span>
                         </td>
                       </tr>
                     ))}
                   </tbody>
                 </Table>
                {renderPagination(foundedDealsPagination.page, foundedDealsPagination.totalPages, handleFoundedDealsPageChange, foundedDealsPagination.totalElements, foundedDealsPagination.size)}
              </Card>
            </Col>
          </Row>
        </>
      )}

      {/* No Data Message */}
      {showingOverdueDeals && (!overdueDeals || !overdueDeals.content || overdueDeals.content.length === 0) && (
        <Row>
          <Col>
            <Card>
              <CardBody className="text-center py-5">
                <h4 className="text-success">No Overdue Deals</h4>
                <p className="text-muted">
                  Great! There are no deals with overdue payments at this time.
                </p>
                <Button
                  color="primary"
                  onClick={() => setShowingOverdueDeals(false)}
                  className="mt-2"
                >
                  <i className="ni ni-bold-left mr-1"></i>
                  Back to All Deals
                </Button>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      {!showingOverdueDeals && (!metrics?.dealBreakdown || !metrics.dealBreakdown.content || metrics.dealBreakdown.content.length === 0) && (
        <Row>
          <Col>
            <Card>
              <CardBody className="text-center py-5">
                <i className="ni ni-chart-bar-32 text-muted" style={{ fontSize: '3rem' }}></i>
                <h4 className="mt-3 text-muted">No Deal Data Available</h4>
                <p className="text-muted">
                  There are no deals with financial data to display at this time.
                </p>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}
    </Container>
  );
};

export default FinancialMetrics;
